import * as React from 'react';

import styled from '@emotion/styled';
import { transparentize } from 'polished';
import { ExpandMoreIcon } from 'react-magma-icons';

import { AccordionIconPosition } from './useAccordion';
import { useIsInverse } from '../../inverse';
import { ThemeInterface } from '../../theme/magma';
import { ThemeContext } from '../../theme/ThemeContext';
import { Spacer } from '../Spacer';
import {
  useAccordionButton,
  UseAccordionButtonProps,
} from './useAccordionButton';
import { Transition } from '../Transition';

export interface AccordionButtonProps
  extends UseAccordionButtonProps,
    React.HTMLAttributes<HTMLButtonElement> {
  /**
   * @children required
   */
  children: React.ReactNode;
  /**
   * For use in components repurposing Accordion with custom keyboard navigation with it's elements.
   * @internal
   */
  customOnKeyDown?: () => void;
  /**
   * @internal
   */
  theme?: ThemeInterface;
}

const StyledButton = styled.button<{
  isInverse?: boolean;
  isExpanded?: boolean;
}>`
  background: transparent;
  border: 0;
  border-top: 1px solid
    ${props =>
      props.isInverse
        ? props.theme.colors.neutral800
        : props.theme.colors.neutral200};
  color: ${props =>
    props.isInverse
      ? props.theme.colors.neutral0
      : props.theme.colors.brand.navy};
  cursor: pointer;
  display: flex;
  font-size: ${props => props.theme.typeScale.size03.fontSize};
  font-family: ${props => props.theme.bodyFont};
  line-height: ${props => props.theme.typeScale.size03.lineHeight};
  font-weight: 600;
  padding: 12px 16px;
  text-align: left;
  width: 100%;

  &:focus {
    outline: 2px solid
      ${props =>
        props.isInverse
          ? props.theme.colors.focusInverse
          : props.theme.colors.focus};
    outline-offset: 0;
  }

  svg {
    color: ${props =>
      props.isInverse
        ? props.theme.colors.neutral0
        : props.theme.colors.brand.navy};
  }

  &&[disabled] {
    color: ${props =>
      props.isInverse
        ? transparentize(0.6, props.theme.colors.neutral0)
        : transparentize(0.4, props.theme.colors.neutral500)};
    cursor: not-allowed;

    svg {
      color: ${props =>
        props.isInverse
          ? transparentize(0.6, props.theme.colors.neutral0)
          : transparentize(0.4, props.theme.colors.neutral500)};
    }
  }
`;

const TextWrapper = styled.span`
  flex-grow: 1;
`;

export const AccordionButton = React.forwardRef<
  HTMLButtonElement,
  AccordionButtonProps
>((props, forwardedRef) => {
  const {
    children,
    testId,
    customOnKeyDown,
    isInverse: isInverseProp,
    ...rest
  } = props;
  const theme = React.useContext(ThemeContext);
  const isInverse = useIsInverse(isInverseProp);

  const {
    iconPosition,
    buttonId,
    isDisabled,
    isExpanded,
    panelId,
    handleClick,
    handleKeyDown,
    ref,
  } = useAccordionButton(props, forwardedRef);

  const caret = (
    <Transition
      isOpen={isExpanded}
      rotate180
      style={{ height: theme.spaceScale.spacing06 }}
    >
      <ExpandMoreIcon />
    </Transition>
  );

  return (
    <StyledButton
      {...rest}
      aria-controls={panelId}
      aria-expanded={Boolean(isExpanded)}
      data-testid={testId}
      disabled={isDisabled}
      id={buttonId}
      isExpanded={isExpanded}
      isInverse={isInverse}
      onClick={handleClick}
      onKeyDown={customOnKeyDown ? customOnKeyDown : handleKeyDown}
      ref={ref}
      theme={theme}
    >
      {iconPosition === AccordionIconPosition.left && (
        <>
          {caret}
          <Spacer size={12} />
        </>
      )}
      <TextWrapper>{children}</TextWrapper>
      {iconPosition === AccordionIconPosition.right && (
        <>
          <Spacer size={12} />
          {caret}
        </>
      )}
    </StyledButton>
  );
});
